/**
 * Credenciais da API do site (spec 013, item 3): o WordPress le planos e checkouts com um token `mtcwp_`. O ADMIN emite
 * (o token aparece uma vez) e revoga. Quem confere e guarda so o hash e o servico de pagamentos.
 */
import { Aviso, Botao, Campo, Carregando, Dialogo, Pilha, Secao, Selo, Tabela, TextoCopiavel, Vazio, formatarDataHora } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { erroDe, useApi, usePapel } from "../api";
import { config } from "../config";
import { DialogoDeAcao } from "./DialogoDeAcao";
import { usePagina } from "../pagina";

const PAG = (caminho: string) => `${config().PAGAMENTOS_URL.replace(/\/$/, "")}${caminho}`;

type Credencial = { id: string; nome: string; prefixo: string; criada_em: string; ultimo_uso_em: string | null; revogada_em: string | null };

export function CredenciaisDoSite() {
  const pagCred = usePagina("pagina_credenciais");
  const { ler, postar } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const admin = pode("ADMIN");
  const q = useQuery({ queryKey: ["credenciais-do-site"], queryFn: () => ler<{ ligada: boolean; itens: Credencial[] }>(PAG("/v1/admin/credenciais-do-site")), enabled: admin });
  const [emitir, setEmitir] = useState(false);
  const [nome, setNome] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [revogar, setRevogar] = useState<Credencial | null>(null);
  const atualizar = () => void qc.invalidateQueries({ queryKey: ["credenciais-do-site"] });
  if (!admin || (q.data && !q.data.ligada)) return null;
  return (
    <Secao rotulo="Integração" titulo="API do site" acoes={<Botao tamanho="pequeno" variante="secundario" onClick={() => { setNome(""); setEmitir(true); }}>Emitir credencial</Botao>}>
      <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>O WordPress lê planos, checkouts e benefícios por esta API, só leitura. Uma credencial por site; revogue quando o site parar de usar ou se o token vazar.</span>
      {q.isLoading ? <Carregando compacto /> : q.isError ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso> : (
        <Tabela
          paginacao={pagCred}
          linhas={q.data?.itens ?? []}
          chave={(c) => c.id}
          vazio={<Vazio titulo="Nenhuma credencial">Emita uma pro site ler os checkouts.</Vazio>}
          colunas={[
            { chave: "nome", rotulo: "Credencial", celula: (c) => <><strong>{c.nome}</strong><span className="mtc-tabela__apoio mtc-mono">mtcwp_{c.prefixo}_...</span></> },
            { chave: "situacao", rotulo: "Situação", celula: (c) => <Selo tom={c.revogada_em ? "neutro" : "sucesso"}>{c.revogada_em ? "Revogada" : "Ativa"}</Selo> },
            { chave: "uso", rotulo: "Último uso", celula: (c) => <span className="mtc-mono">{c.ultimo_uso_em ? formatarDataHora(c.ultimo_uso_em) : "nunca"}</span> },
            { chave: "criada", rotulo: "Emitida em", celula: (c) => <span className="mtc-mono">{formatarDataHora(c.criada_em)}</span> },
            { chave: "acoes", rotulo: "", celula: (c) => (c.revogada_em ? null : <Botao tamanho="pequeno" variante="fantasma" onClick={() => setRevogar(c)}>Revogar</Botao>) },
          ]}
        />
      )}
      <DialogoDeAcao
        aberto={emitir}
        aoFechar={() => setEmitir(false)}
        titulo="Emitir credencial"
        descricao="O token aparece uma vez só, na próxima tela. Guarde direto na configuração do WordPress."
        botao="Emitir"
        podeEnviar={nome.trim().length >= 3}
        aoEnviar={async () => setToken((await postar<{ token: string }>(PAG("/v1/admin/credenciais-do-site"), { nome: nome.trim() })).token)}
        aoConcluir={() => { setEmitir(false); atualizar(); }}
      >
        <Campo rotulo="Nome" valor={nome} aoMudar={setNome} placeholder="Ex.: WordPress mtcprop.com.br" maxLength={80} />
      </DialogoDeAcao>
      <Dialogo aberto={Boolean(token)} aoFechar={() => setToken(null)} titulo="Copie o token agora" descricao="Depois de fechar, ninguém consegue ver este token de novo, nem a equipe. Perdeu, emita outro e revogue este.">
        <Pilha espaco={3}>
          {token && <TextoCopiavel valor={token} rotulo="Copiar token" />}
          <div className="mtc-dialogo__rodape"><Botao onClick={() => setToken(null)}>Já copiei</Botao></div>
        </Pilha>
      </Dialogo>
      <DialogoDeAcao
        aberto={Boolean(revogar)}
        aoFechar={() => setRevogar(null)}
        titulo="Revogar credencial"
        descricao={revogar ? `${revogar.nome} para de funcionar na hora. O site que usa este token passa a receber erro de credencial.` : undefined}
        botao="Revogar"
        variante="perigo"
        aoEnviar={() => postar(PAG(`/v1/admin/credenciais-do-site/${revogar!.id}/revogar`))}
        aoConcluir={() => { setRevogar(null); atualizar(); }}
      />
    </Secao>
  );
}
